import type { ReactNode } from 'react'
import { Status } from './Status'
import { Tick } from './StageTrack'

// The evidence list: what an inspector will ask for, and where each item is.
//
// # FOUR STATES, AFTER THE GOV.UK TASK LIST
//
// Done is plain text, because a finished item needs nothing and should not
// compete for the eye. Missing is a critical label, in progress is an info
// label that carries its note ("2 of 4"), and an item that cannot start yet is
// grey "Not yet" with no action, because offering an action that cannot work
// is how people learn to stop trusting the list.
//
// The rows share one set of columns (a subgrid), so names, owners, states and
// actions line up down the list even when only some rows have an action.

export type TaskState = 'done' | 'missing' | 'progress' | 'waiting'

type Task = { key: string; name: string; owner?: string; state: TaskState; note?: string; action?: ReactNode }

function label(t: Task): ReactNode {
  switch (t.state) {
    case 'done':
      return <Status tone="plain">Done</Status>
    case 'missing':
      return <Status tone="crit">Missing</Status>
    case 'progress':
      return <Status tone="info">{t.note ?? 'In progress'}</Status>
    case 'waiting':
      return <Status tone="plain">Not yet</Status>
  }
}

export function TaskList({ items }: { items: Task[] }) {
  return (
    <ul className="kit-tasks">
      {items.map((t) => (
        <li key={t.key} className={'kit-task kit-task-' + t.state}>
          <span className="kit-task-icon" aria-hidden="true">
            {t.state === 'done' && <Tick />}
          </span>
          <span className="kit-task-name">
            {t.name}
            {t.note && t.state !== 'progress' && <small>{t.note}</small>}
          </span>
          <span className="kit-task-owner">{t.owner ?? ''}</span>
          <span className="kit-task-state">{label(t)}</span>
          <span className="kit-task-action">{t.state !== 'waiting' ? t.action : null}</span>
        </li>
      ))}
    </ul>
  )
}
